// The MIT License (MIT)
//
// Copyright (c) 2016-2026 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import './sass/desktop.scss';

import angular from 'angular';
import {isEventUsingCtrlKey} from 'ngeo/utils';
import gmfControllersAbstractDesktopController, {
  AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController';
import pronatura_geoportalBase from '../pronatura_geoportalmodule';

import pnEditingModule from '../editing/module.js';
import '../pronaturaAttributes/load_and_override.js';
import pnParzellenFormsPopup from '../pronaturaParzellenFormsPopup/component.js';

/**
 * @private
 */
class Controller extends AbstractDesktopController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   */
  constructor($scope, $injector) {
    super($scope, $injector);

    // Static "not used" functions should be in the window because otherwise
    // closure remove them. "export" tag doesn't work on static function below,
    // we "export" them as externs in the pnx options file.
    const pnx = window.pnx || {};
    // @ts-ignore: We do want to define a new property on `window`.
    window.pnx = pnx;

    this.pnParzellenFormsPopupSource_ = $injector.get('pnParzellenFormsPopupSource');

    /**
     * Static function to set the pnParzellenFormsPopupSource (that opens a popup with the
     * forms).
     * @param {number} id of the parzellen.
     * @param {string} type of document.
     */
    pnx.showParzellenFormsPopup = (id, type) => {
      this.pnParzellenFormsPopupSource_.id = id !== null && id !== undefined ? `${id}` : null;
      this.pnParzellenFormsPopupSource_.type = type !== null && type !== undefined ? `${type}` : null;
      $scope.$apply();
    };
  }

  /**
   * @param {JQuery.Event} event keydown event.
   */
  onKeydown(event) {
    if (event && isEventUsingCtrlKey(event) && event.key === 'p') {
      this.printPanelActive = true;
      event.preventDefault();
    }
  }
}

Controller.$inject = ['$scope', '$injector'];

/**
 * @hidden
 */
const pronatura_geoportalModule = angular.module('Appdesktop', [
  pronatura_geoportalBase.name,
  gmfControllersAbstractDesktopController.name,
  pnEditingModule.name,
  pnParzellenFormsPopup.name,
]);

pronatura_geoportalModule.value('pnParzellenFormsPopupSource', {
  id: null,
  type: null,
});
pronatura_geoportalModule.value('gmfContextualdatacontentTemplateUrl', 'gmf/contextualdata');
pronatura_geoportalModule.run([
  /** @param {angular.ITemplateCacheService} $templateCache */
  '$templateCache',
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('gmf/contextualdata', require('./contextualdata.html'));
  },
]);

pronatura_geoportalModule.controller('DesktopController', Controller);

export default pronatura_geoportalModule;
